import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, Filter, Search, Languages, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Valoracion } from '../components/Valoracion';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfesionalesPage = () => {
  const [professionals, setProfessionals] = useState([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prosResponse, specsResponse] = await Promise.all([
          axios.get(`${API_URL}/api/profesionales`),
          axios.get(`${API_URL}/api/especialidades`)
        ]);
        
        setProfessionals(prosResponse.data);
        setFilteredProfessionals(prosResponse.data);
        setSpecialties(specsResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...professionals];

    if (selectedSpecialty && selectedSpecialty !== 'all') {
      filtered = filtered.filter(p => 
        p.specialties.includes(selectedSpecialty)
      );
    }

    if (selectedLanguage && selectedLanguage !== 'all') {
      filtered = filtered.filter(p => 
        p.languages.includes(selectedLanguage)
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(term) ||
        p.specialties.some(s => s.toLowerCase().includes(term))
      );
    }

    setFilteredProfessionals(filtered);
  }, [selectedSpecialty, selectedLanguage, searchTerm, professionals]);

  const languages = [...new Set(professionals.flatMap(p => p.languages))];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="bg-teal-600 py-16 md:py-24">
        <div className="container-max mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-white/20 text-white hover:bg-white/20 mb-4">
              <Shield className="w-3 h-3 mr-1" />
              Profesionales Verificados
            </Badge>
            <h1 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
              Nuestros profesionales
            </h1>
            <p className="text-lg text-teal-100 leading-relaxed">
              Todos nuestros psicólogos están titulados, con registro profesional verificado y comprometidos con tu bienestar y privacidad.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b sticky top-20 z-40">
        <div className="container-max mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o especialidad..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-professionals"
              />
            </div>
            
            <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-specialty">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Especialidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las especialidades</SelectItem>
                {specialties.map((spec) => (
                  <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-language">
                <Languages className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Idioma" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los idiomas</SelectItem>
                {languages.map((lang) => (
                  <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Professionals Grid */}
      <section className="section-padding">
        <div className="container-max mx-auto">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="h-48 w-full" />
                  <div className="p-6 space-y-4">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </Card>
              ))}
            </div>
          ) : filteredProfessionals.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-slate-600 mb-4">
                No encontramos profesionales con esos criterios.
              </p>
              <Button variant="outline" onClick={() => {
                setSelectedSpecialty('all');
                setSelectedLanguage('all');
                setSearchTerm('');
              }}>
                Limpiar filtros
              </Button>
            </div>
          ) : (
            <>
              <p className="text-slate-600 mb-6">
                {filteredProfessionals.length} profesional{filteredProfessionals.length !== 1 ? 'es' : ''} encontrado{filteredProfessionals.length !== 1 ? 's' : ''}
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProfessionals.map((professional) => (
                  <Card 
                    key={professional.id} 
                    className="professional-card overflow-hidden border-0 shadow-soft"
                    data-testid={`professional-card-${professional.id}`}
                  >
                    <div className="relative">
                      <div className="aspect-[16/10] overflow-hidden">
                        <img 
                          src={professional.profile_image} 
                          alt={professional.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      {professional.verified && (
                        <Badge className="absolute top-4 right-4 bg-sage-600 text-white">
                          <Shield className="w-3 h-3 mr-1" />
                          Verificado
                        </Badge>
                      )}
                    </div>
                    
                    <CardContent className="p-6">
                      <Valoracion
                        rating={professional.rating}
                        cantidad={professional.rating_count}
                        sesiones={professional.sessions_count}
                        className="mb-3"
                      />

                      <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                        {professional.name}
                      </h3>

                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                        {professional.bio}
                      </p>

                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {professional.specialties.slice(0, 3).map((spec) => (
                          <Badge key={spec} variant="secondary" className="text-xs bg-teal-50 text-teal-700">
                            {spec}
                          </Badge>
                        ))}
                        {professional.specialties.length > 3 && (
                          <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-600">
                            +{professional.specialties.length - 3}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                        <div className="flex items-center gap-1">
                          <Languages className="w-4 h-4" />
                          <span>{professional.languages.join(', ')}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t">
                        <div>
                          <p className="text-xs text-slate-500">Desde</p>
                          <p className="text-lg font-semibold text-slate-900">
                            ${professional.price_from.toLocaleString('es-CL')} CLP
                          </p>
                        </div>
                        <Link to={`/agendar?profesional=${professional.id}`}>
                          <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                            Agendar
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Education Verification */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="glass-card p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                  Verificación de credenciales
                </h2>
                <p className="text-slate-600 mb-6">
                  Todos nuestros profesionales pasan por un riguroso proceso de verificación. Validamos sus títulos universitarios, registro profesional en el Colegio de Psicólogos, y experiencia clínica.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-700">
                    <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center">
                      <Shield className="w-3 h-3 text-sage-600" />
                    </div>
                    Título universitario verificado
                  </li>
                  <li className="flex items-center gap-3 text-slate-700">
                    <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center">
                      <Shield className="w-3 h-3 text-sage-600" />
                    </div>
                    Registro profesional activo
                  </li>
                  <li className="flex items-center gap-3 text-slate-700">
                    <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center">
                      <Shield className="w-3 h-3 text-sage-600" />
                    </div>
                    Experiencia clínica comprobada
                  </li>
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="w-48 h-48 rounded-full bg-sage-100 flex items-center justify-center">
                  <Shield className="w-24 h-24 text-sage-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfesionalesPage;
